import { Button, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useReducer } from 'react';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import ConfirmationDialog from '~/components/invoices/ConfirmationDialog';
import {
  ApproveDialogContent,
  PartialApproveDialogContent,
  RejectDialogContent,
  ResetDialogContent,
} from '~/components/invoices/InvoiceActionsDialogsContent';
import {
  buildRuleSuggestionMessage,
  type ConsultantDecision,
  isRuleSuggestionDisagreement,
} from '~/components/invoices/ruleSuggestionNudge';
import { useOttoOptional } from '~/components/otto/OttoProvider';
import type {
  ApproveInvoiceForm,
  Invoice,
  PartialApproveInvoiceForm,
  RejectInvoiceForm,
  ResetInvoiceForm,
} from '~/data/schemas/invoice';
import { InvoiceActionType, isInvoiceFinal } from '~/data/schemas/invoice';
import { BUTTON_SX } from '~/theme';
import { ACTIONS_ON_SPENDING, canAccess } from '~/utils/role';

interface InvoiceActionsProps {
  onApprove: (formData: ApproveInvoiceForm) => Promise<void>;
  onReset: (formData: ResetInvoiceForm) => Promise<void>;
  onReject: (formData: RejectInvoiceForm) => Promise<void>;
  onPartial: (formData: PartialApproveInvoiceForm) => Promise<void>;
  onRequestData: () => Promise<void>;
  revalidateNotes?: () => void;
  invoice?: Invoice;
  /**
   * `footer` is the drawer's full-width bar across the bottom. `header` stacks the
   * same buttons into a full-width column in the detail page's header, so the
   * decision is reachable without scrolling to the end of a tab.
   */
  variant?: 'footer' | 'header' | 'inline';
}

type InvoiceAction =
  | {
      type: 'SHOW_FORM';
      invoiceActionType:
        | InvoiceActionType.PARTIAL
        | InvoiceActionType.REJECT
        | InvoiceActionType.RESET
        | InvoiceActionType.APPROVE;
    }
  | {
      type: 'SUBMIT_FORM';
      invoiceActionType: InvoiceActionType;
    }
  | {
      type: 'FORM_SUBMITTED';
    }
  | {
      type: 'FORM_DISMISSED';
    };

type InvoiceActionState =
  | {
      type: 'initial';
    }
  | {
      type: 'show_form';
      invoiceActionType: InvoiceActionType;
    }
  | {
      type: 'submitting_form';
      invoiceActionType: InvoiceActionType;
    }
  | { type: 'final'; invoiceActionType: InvoiceActionType };

const reducer = (state: InvoiceActionState, action: InvoiceAction): InvoiceActionState => {
  switch (action.type) {
    case 'SHOW_FORM':
      return { type: 'show_form', invoiceActionType: action.invoiceActionType };
    case 'SUBMIT_FORM':
      return {
        type: 'submitting_form',
        invoiceActionType: action.invoiceActionType,
      };
    case 'FORM_SUBMITTED':
      // Tolerate FORM_SUBMITTED arriving when we're already past
      // `submitting_form` — e.g. StrictMode replays a child effect that
      // re-fires its onSubmit, or the user double-clicks the submit
      // button. The previous `throw` here would unmount the whole tree
      // for what is a benign duplicate dispatch (and was the source of
      // a flaky test in this file's __tests__).
      if (state.type !== 'submitting_form') {
        return state;
      }
      return {
        ...state,
        type: 'final',
      };
    case 'FORM_DISMISSED':
      return { type: 'initial' };
  }
};

export default function InvoiceActions({
  onApprove,
  onReset,
  onPartial,
  onReject,
  onRequestData,
  revalidateNotes,
  invoice,
  variant = 'footer',
}: InvoiceActionsProps) {
  const [state, dispatch] = useReducer(reducer, { type: 'initial' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const buttonSize = isMobile || variant === 'inline' ? 'small' : 'medium';
  const {
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const { track } = useAnalytics();
  const otto = useOttoOptional();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const trackInvoiceAction = useCallback(
    // Mirror the backend's InvoiceActionType verbatim (case-folded) so
    // Mixpanel events line up 1:1 with audit-log entries.
    (action: 'approve' | 'reject' | 'partial' | 'request_data' | 'reset') => {
      if (!invoice?.id) return;
      track('invoice_action_taken', { invoice_id: invoice.id, action });
    },
    [invoice, track],
  );

  // After a decision is saved, if it disagrees with Lift AI's review, offer a
  // post-finalize, non-blocking nudge to draft a contract review rule. Fires
  // only on genuine disagreement (mirrors the backend classifier) and only when
  // Otto is available, so it tapers as the AI improves. The chat opens as a
  // clean follow-up — the decision flow is already complete, so nothing is
  // interrupted or cancelled.
  const maybeOfferRuleSuggestion = useCallback(
    (consultant: ConsultantDecision, ctx: { reason?: string; rejectionCategory?: string[] }) => {
      const aiStatus = invoice?.insights?.status;
      if (!otto || !invoice || !aiStatus || !isRuleSuggestionDisagreement(consultant, aiStatus)) {
        return;
      }
      track('rule_suggestion_nudge_shown', {
        invoice_id: invoice.id,
        consultant_status: consultant,
        ai_status: aiStatus,
      });
      const message = buildRuleSuggestionMessage({
        invoiceNumber: invoice.number,
        propertyName: invoice.property?.name ?? 'this property',
        invoiceId: invoice.id,
        consultant,
        aiStatus,
        reason: ctx.reason,
        rejectionCategory: ctx.rejectionCategory,
      });
      enqueueSnackbar(
        'Your decision differs from Lift AI. Add a review rule so it gets this right next time?',
        {
          variant: 'info',
          persist: true,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          action: (key) => (
            <>
              <Button
                color="inherit"
                size="small"
                sx={BUTTON_SX}
                onClick={() => {
                  // Start the chat first so we can stamp the new session_id on
                  // the click event — it's the last event carrying invoice_id,
                  // so it bridges the invoice funnel to the chat-side events.
                  const sessionId = otto.startConversation(message, { invoiceId: invoice.id });
                  track('rule_suggestion_nudge_clicked', {
                    invoice_id: invoice.id,
                    session_id: sessionId,
                    consultant_status: consultant,
                    ai_status: aiStatus,
                  });
                  closeSnackbar(key);
                }}
              >
                Suggest rules
              </Button>
              <Button
                color="inherit"
                size="small"
                sx={BUTTON_SX}
                onClick={() => {
                  track('rule_suggestion_nudge_dismissed', { invoice_id: invoice.id });
                  closeSnackbar(key);
                }}
              >
                Dismiss
              </Button>
            </>
          ),
        },
      );
    },
    [otto, invoice, track, enqueueSnackbar, closeSnackbar],
  );

  const showConfirmationDialog =
    invoice &&
    state.type === 'final' &&
    [InvoiceActionType.APPROVE, InvoiceActionType.PARTIAL, InvoiceActionType.REJECT].includes(
      state.invoiceActionType,
    );
  const isSaving = state.type === 'submitting_form';
  const showForm = (state.type === 'show_form' || isSaving) && invoice;
  const buttonsDisabled = isSaving || !invoice;

  // The first have a different flow as there is no intermediary dialog
  const handleRequestData = useCallback(async () => {
    dispatch({
      type: 'SUBMIT_FORM',
      invoiceActionType: InvoiceActionType.REQUEST_DATA,
    });
    await onRequestData();
    trackInvoiceAction('request_data');
    dispatch({ type: 'FORM_SUBMITTED' });
  }, [onRequestData, trackInvoiceAction]);

  const handleApprove = useCallback(async () => {
    dispatch({ type: 'SHOW_FORM', invoiceActionType: InvoiceActionType.APPROVE });
  }, []);

  const handleReject = useCallback(async () => {
    dispatch({ type: 'SHOW_FORM', invoiceActionType: InvoiceActionType.REJECT });
  }, []);

  const handlePartial = useCallback(async () => {
    dispatch({ type: 'SHOW_FORM', invoiceActionType: InvoiceActionType.PARTIAL });
  }, []);

  const handleClose = useCallback(() => {
    dispatch({ type: 'FORM_DISMISSED' });
  }, []);

  const handleReset = useCallback(async () => {
    dispatch({ type: 'SHOW_FORM', invoiceActionType: InvoiceActionType.RESET });
  }, []);

  const handleResetSubmit = useCallback(
    async (data: ResetInvoiceForm) => {
      dispatch({ type: 'SUBMIT_FORM', invoiceActionType: InvoiceActionType.RESET });
      await onReset(data);
      trackInvoiceAction('reset');
      dispatch({ type: 'FORM_SUBMITTED' });
    },
    [onReset, trackInvoiceAction],
  );

  const handleApproveSubmit = useCallback(
    async (data: ApproveInvoiceForm) => {
      dispatch({
        type: 'SUBMIT_FORM',
        invoiceActionType: InvoiceActionType.APPROVE,
      });
      await onApprove(data);
      trackInvoiceAction('approve');
      dispatch({ type: 'FORM_SUBMITTED' });
      maybeOfferRuleSuggestion('approved', { reason: data.reason });
    },
    [onApprove, trackInvoiceAction, maybeOfferRuleSuggestion],
  );

  const handlePartialApproveSubmit = useCallback(
    async (data: PartialApproveInvoiceForm) => {
      dispatch({ type: 'SUBMIT_FORM', invoiceActionType: InvoiceActionType.PARTIAL });
      await onPartial(data);
      trackInvoiceAction('partial');
      dispatch({ type: 'FORM_SUBMITTED' });
      maybeOfferRuleSuggestion('partial-approval', {
        reason: data.reason,
        rejectionCategory: data.rejectionCategory,
      });
    },
    [onPartial, trackInvoiceAction, maybeOfferRuleSuggestion],
  );

  const handleRejectSubmit = useCallback(
    async (data: RejectInvoiceForm) => {
      dispatch({ type: 'SUBMIT_FORM', invoiceActionType: InvoiceActionType.REJECT });
      await onReject(data);
      trackInvoiceAction('reject');
      dispatch({ type: 'FORM_SUBMITTED' });
      maybeOfferRuleSuggestion('rejected', {
        reason: data.reason,
        rejectionCategory: data.rejectionCategory,
      });
    },
    [onReject, trackInvoiceAction, maybeOfferRuleSuggestion],
  );

  return (
    <>
      <Toolbar
        disableGutters={variant !== 'footer'}
        sx={{
          ...(variant === 'inline'
            ? { minHeight: 'auto', flexWrap: 'wrap', gap: 1 }
            : variant === 'header'
              ? {
                  // A stacked column, Approve first: the decisions are ranked, not peers, and stacking
                  // keeps them out of the header's horizontal budget.
                  minHeight: 'auto',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1,
                }
              : {
                  borderTop: 1,
                  borderColor: 'divider',
                  alignItems: 'center',
                  justifyContent: 'space-around',
                }),
          ...(!invoice && { opacity: 0.5, cursor: 'not-allowed' }),
        }}
      >
        {!invoice || !isInvoiceFinal(invoice) ? (
          <>
            {canAccess(userRole, ACTIONS_ON_SPENDING) && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  sx={BUTTON_SX}
                  disabled={buttonsDisabled}
                  onClick={handleApprove}
                  size={buttonSize}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  sx={BUTTON_SX}
                  disabled={buttonsDisabled}
                  onClick={handleReject}
                  size={buttonSize}
                >
                  Reject
                </Button>
                {!invoice?.parentInvoice && (
                  <>
                    <Button
                      variant="contained"
                      color="warning"
                      sx={BUTTON_SX}
                      disabled={buttonsDisabled}
                      onClick={handlePartial}
                      size={buttonSize}
                    >
                      Partial
                    </Button>
                    <Button
                      variant="contained"
                      color="info"
                      sx={BUTTON_SX}
                      disabled={buttonsDisabled}
                      onClick={handleRequestData}
                      size={buttonSize}
                    >
                      Request Data
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          canAccess(userRole, ACTIONS_ON_SPENDING) && (
            <Button
              variant="contained"
              color="error"
              sx={BUTTON_SX}
              disabled={buttonsDisabled}
              onClick={handleReset}
              size={buttonSize}
            >
              Undo Final Stamp
            </Button>
          )
        )}
      </Toolbar>

      {showForm ? (
        <>
          {state.invoiceActionType === InvoiceActionType.APPROVE && (
            <ApproveDialogContent
              isSaving={isSaving}
              invoice={invoice}
              onClose={handleClose}
              onSubmit={handleApproveSubmit}
              revalidateNotes={revalidateNotes}
            />
          )}
          {state.invoiceActionType === InvoiceActionType.PARTIAL && (
            <PartialApproveDialogContent
              isSaving={isSaving}
              invoice={invoice}
              onClose={handleClose}
              onSubmit={handlePartialApproveSubmit}
              revalidateNotes={revalidateNotes}
            />
          )}
          {state.invoiceActionType === InvoiceActionType.REJECT && (
            <RejectDialogContent
              isSaving={isSaving}
              invoice={invoice}
              onClose={handleClose}
              onSubmit={handleRejectSubmit}
              revalidateNotes={revalidateNotes}
            />
          )}
          {state.invoiceActionType === InvoiceActionType.RESET && (
            <ResetDialogContent
              isSaving={isSaving}
              invoice={invoice}
              onClose={handleClose}
              onSubmit={handleResetSubmit}
              revalidateNotes={revalidateNotes}
            />
          )}
        </>
      ) : null}

      {showConfirmationDialog ? (
        <ConfirmationDialog
          onClose={handleClose}
          actionType={state.invoiceActionType}
          invoice={invoice}
        />
      ) : null}
    </>
  );
}
